import React, { useState, useEffect } from 'react';
import { Check, Download, Building2, ShoppingCart, Briefcase, ChevronDown, ChevronUp, Save, CheckCircle, XCircle } from 'lucide-react';
import { ContractView } from './ContractView';
import { RejectionModal } from './RejectionModal';
import CustomSelect from './CustomSelect';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { markOpportunityLost } from '../lib/crmService';
import jsPDF from 'jspdf';
import {
  PDF_TYPOGRAPHY,
  hexToRgb,
  checkPageBreak,
  setColor,
  renderPageTitle,
  renderSectionHeader,
  renderServiceItem,
  renderAdditionalItem,
  renderFeaturesToPDF,
} from '../lib/pdfConfig';
import { calculateWebDiscount, calculateMarketingDiscount, type ProductBreakdown } from '../lib/discountUtils';

interface ServicePricing {
  id: string;
  category: string;
  client_type: string | null;
  tier_key: string;
  name: string;
  price: number;
  additional_price: number | null;
  description: string | null;
  show_description: boolean;
  features: any;
  key_deliverables: string[];
  payment_schedule: Array<{ percentage: number; description: string }>;
}

interface AdditionalPricing {
  id: string;
  item_key: string;
  name: string;
  price: number;
  description: string | null;
  features: any;
}

interface SupportPlan {
  id: string;
  tier_key: string;
  name: string;
  hours: number;
  price: number;
  description: string | null;
  features: any;
}

interface Brand {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  contact_info: any;
  terms_text: string;
}

interface Estimate {
  id: string;
  brand_id: string;
  client_name: string;
  company_name: string | null;
  email: string | null;
  client_type: string;
  config: any;
  totals: any;
  created_at: string;
}

export default function PublicCalculator() {
  const toast = useToast();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [servicePricing, setServicePricing] = useState<ServicePricing[]>([]);
  const [additionalPricing, setAdditionalPricing] = useState<AdditionalPricing[]>([]);
  const [supportPlans, setSupportPlans] = useState<SupportPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const [clientName, setClientName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientType, setClientType] = useState('local');
  const [includeWeb, setIncludeWeb] = useState(false);
  const [includeMarketing, setIncludeMarketing] = useState(true);
  const [pkg, setPkg] = useState('business');
  const [seoTier, setSeoTier] = useState<string | null>(null);
  const [emailTier, setEmailTier] = useState<string | null>(null);
  const [selectedAdPlatforms, setSelectedAdPlatforms] = useState<string[]>([]);
  const [croTier, setCroTier] = useState<string | null>(null);
  const [multiLocation, setMultiLocation] = useState(0);
  const [addOns, setAddOns] = useState<string[]>([]);
  const [customPages, setCustomPages] = useState(0);
  const [sameLayoutPages, setSameLayoutPages] = useState(0);
  const [extraRevisions, setExtraRevisions] = useState(0);
  const [adSpend, setAdSpend] = useState(5000);
  const [selectedSupportPlan, setSelectedSupportPlan] = useState<string | null>(null);
  const [expandedPackage, setExpandedPackage] = useState<string | null>(null);
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  const [appliedWebDiscount, setAppliedWebDiscount] = useState<any>(null);
  const [discountError, setDiscountError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [proposalStatus, setProposalStatus] = useState('pending');
  const [showContract, setShowContract] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [proposalResponse, setProposalResponse] = useState<any>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    try {
      setLoading(true);
      const params = new URLSearchParams(window.location.search);
      const estimateId = params.get('estimate') || params.get('testimate');
      const brandId = params.get('brand');

      if (estimateId) {
        const { data: estimateData, error: estimateError } = await supabase
          .from('estimates')
          .select('*')
          .eq('shareable_id', estimateId)
          .maybeSingle();

        if (estimateError) throw estimateError;
        if (!estimateData) {
          toast.error('The estimate you are looking for could not be found. Please check the link and try again.', 'Estimate Not Found');
          return;
        }

        setEstimate(estimateData);

        const { data: brandData, error: brandError } = await supabase
          .from('brands')
          .select('*')
          .eq('id', estimateData.brand_id)
          .maybeSingle();

        if (brandError) throw brandError;
        if (!brandData) {
          toast.error('The brand information could not be loaded. Please try again later.', 'Brand Not Found');
          return;
        }

        setBrand(brandData);

        const [servicesRes, additionalRes, supportRes] = await Promise.all([
          supabase.from('service_pricing').select('*').eq('brand_id', estimateData.brand_id),
          supabase.from('additional_pricing').select('*').eq('brand_id', estimateData.brand_id),
          supabase.from('support_plans').select('*').eq('brand_id', estimateData.brand_id).order('price'),
        ]);

        if (servicesRes.error) throw servicesRes.error;
        if (additionalRes.error) throw additionalRes.error;
        if (supportRes.error) throw supportRes.error;

        setServicePricing(servicesRes.data || []);
        setAdditionalPricing(additionalRes.data || []);
        setSupportPlans(supportRes.data || []);

        const config = estimateData.config;
        setClientName(estimateData.client_name);
        setCompanyName(estimateData.company_name || '');
        setClientEmail(estimateData.email || '');
        setClientType(estimateData.client_type);
        if (config.includeWeb !== undefined) setIncludeWeb(config.includeWeb);
        if (config.includeMarketing !== undefined) setIncludeMarketing(config.includeMarketing);
        if (config.pkg) setPkg(config.pkg);
        if (config.seoTier) setSeoTier(config.seoTier);
        if (config.emailTier) setEmailTier(config.emailTier);
        if (config.selectedAdPlatforms) setSelectedAdPlatforms(config.selectedAdPlatforms);
        if (config.croTier) setCroTier(config.croTier);
        if (config.multiLocation !== undefined) setMultiLocation(config.multiLocation);
        if (config.addOns) setAddOns(config.addOns);
        if (config.customPages !== undefined) setCustomPages(config.customPages);
        if (config.sameLayoutPages !== undefined) setSameLayoutPages(config.sameLayoutPages);
        if (config.extraRevisions !== undefined) setExtraRevisions(config.extraRevisions);
        if (config.adSpend !== undefined) setAdSpend(config.adSpend);
        if (config.selectedSupportPlan) setSelectedSupportPlan(config.selectedSupportPlan);

        if (config.webDiscount) {
          const { data: webDiscData } = await supabase
            .from('discount_codes')
            .select('*')
            .eq('brand_id', estimateData.brand_id)
            .eq('code', config.webDiscount)
            .eq('active', true)
            .maybeSingle();
          if (webDiscData) setAppliedWebDiscount(webDiscData);
        }

        if (config.marketingDiscount) {
          const { data: mktDiscData } = await supabase
            .from('discount_codes')
            .select('*')
            .eq('brand_id', estimateData.brand_id)
            .eq('code', config.marketingDiscount)
            .eq('active', true)
            .maybeSingle();
          if (mktDiscData) setAppliedDiscount(mktDiscData);
        }

        const { data: responseData } = await supabase
          .from('proposal_responses')
          .select('*')
          .eq('estimate_id', estimateData.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (responseData) {
          setProposalResponse(responseData);
          setProposalStatus(responseData.status);
        }
      } else if (brandId) {
        const { data: brandData, error: brandError } = await supabase
          .from('brands')
          .select('*')
          .eq('id', brandId)
          .maybeSingle();

        if (brandError) throw brandError;
        if (!brandData) {
          toast.error('The brand information could not be loaded. Please try again later.', 'Brand Not Found');
          return;
        }

        setBrand(brandData);

        const [servicesRes, additionalRes, supportRes] = await Promise.all([
          supabase.from('service_pricing').select('*').eq('brand_id', brandId),
          supabase.from('additional_pricing').select('*').eq('brand_id', brandId),
          supabase.from('support_plans').select('*').eq('brand_id', brandId).order('price'),
        ]);

        if (servicesRes.error) throw servicesRes.error;
        if (additionalRes.error) throw additionalRes.error;
        if (supportRes.error) throw supportRes.error;

        setServicePricing(servicesRes.data || []);
        setAdditionalPricing(additionalRes.data || []);
        setSupportPlans(supportRes.data || []);

        const config = params.get('config');
        if (config) {
          try {
            const preselect = JSON.parse(decodeURIComponent(config));
            if (preselect.clientType) setClientType(preselect.clientType);
            if (preselect.includeWeb !== undefined) setIncludeWeb(preselect.includeWeb);
            if (preselect.includeMarketing !== undefined) setIncludeMarketing(preselect.includeMarketing);
            if (preselect.pkg) setPkg(preselect.pkg);
            if (preselect.seoTier) setSeoTier(preselect.seoTier);
            if (preselect.selectedSupportPlan) setSelectedSupportPlan(preselect.selectedSupportPlan);
            if (preselect.selectedAdPlatforms) setSelectedAdPlatforms(preselect.selectedAdPlatforms);
          } catch (e) {
            console.error('Error parsing config:', e);
          }
        }
      } else {
        toast.error('The share link you are using is invalid or has expired.', 'Invalid Share Link');
        return;
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('There was an error loading the calculator. Please try again later.', 'Error Loading Calculator');
    } finally {
      setLoading(false);
    }
  }

  const fmt = (n: number) => '$' + n.toLocaleString();

  function getServicesByCategory(category: string, clientType?: string) {
    return servicePricing.filter(
      s => s.category === category && (!clientType || s.client_type === clientType || s.client_type === null)
    );
  }

  function getAdditionalPrice(itemKey: string): number {
    return additionalPricing.find(p => p.item_key === itemKey)?.price || 0;
  }

  const calcWeb = () => {
    if (!includeWeb) return { total: 0, originalTotal: 0, discount: 0 };

    const breakdown: ProductBreakdown[] = [];
    const selectedKeys: string[] = [];

    const pkgService = getServicesByCategory('website_packages').find(s => s.tier_key === pkg);
    if (pkgService) {
      breakdown.push({
        category: 'website_packages',
        tier_key: pkgService.tier_key,
        price: pkgService.price,
        name: pkgService.name
      });
      selectedKeys.push(pkgService.tier_key);
    }

    addOns.forEach(id => {
      const addon = getServicesByCategory('web_addons').find(s => s.tier_key === id);
      if (addon) {
        breakdown.push({
          category: 'web_addons',
          tier_key: addon.tier_key,
          price: addon.price,
          name: addon.name
        });
        selectedKeys.push(addon.tier_key);
      }
    });

    const customPagePrice = customPages * getAdditionalPrice('custom_page');
    if (customPagePrice > 0) {
      breakdown.push({
        category: 'web_addons',
        tier_key: 'custom_page',
        price: customPagePrice,
        name: 'Custom Pages'
      });
      selectedKeys.push('custom_page');
    }

    const sameLayoutPagePrice = sameLayoutPages * getAdditionalPrice('same_layout_page');
    if (sameLayoutPagePrice > 0) {
      breakdown.push({
        category: 'web_addons',
        tier_key: 'same_layout_page',
        price: sameLayoutPagePrice,
        name: 'Same Layout Pages'
      });
      selectedKeys.push('same_layout_page');
    }

    const extraRevisionsPrice = extraRevisions * getAdditionalPrice('extra_revision');
    if (extraRevisionsPrice > 0) {
      breakdown.push({
        category: 'web_addons',
        tier_key: 'extra_revision',
        price: extraRevisionsPrice,
        name: 'Extra Revisions'
      });
      selectedKeys.push('extra_revision');
    }

    const originalTotal = breakdown.reduce((sum, item) => sum + item.price, 0);
    let discAmount = 0;

    if (appliedWebDiscount) {
      const discountResult = calculateWebDiscount(
        appliedWebDiscount,
        breakdown,
        selectedKeys
      );
      discAmount = discountResult.totalDiscount;
    }

    const total = originalTotal - discAmount;
    return { total, originalTotal, discount: discAmount };
  };

  const calcMarketing = () => {
    if (!includeMarketing) return { monthly: 0, setup: 0, first: 0, disc: 0 };

    const breakdown: ProductBreakdown[] = [];
    const selectedKeys: string[] = [];

    if (selectedSupportPlan) {
      const plan = supportPlans.find(p => p.tier_key === selectedSupportPlan);
      if (plan) {
        breakdown.push({
          category: 'support_plans',
          tier_key: plan.tier_key,
          price: plan.price,
          name: plan.name
        });
        selectedKeys.push(plan.tier_key);
      }
    }

    if (seoTier) {
      const seo = getServicesByCategory('seo_services', clientType).find(s => s.tier_key === seoTier);
      if (seo) {
        breakdown.push({
          category: 'seo_services',
          tier_key: seo.tier_key,
          price: seo.price,
          name: seo.name
        });
        selectedKeys.push(seo.tier_key);
      }
    }

    if (emailTier) {
      const email = getServicesByCategory('email_services', clientType).find(s => s.tier_key === emailTier);
      if (email) {
        breakdown.push({
          category: 'email_services',
          tier_key: email.tier_key,
          price: email.price,
          name: email.name
        });
        selectedKeys.push(email.tier_key);
      }
    }

    if (selectedAdPlatforms.length > 0) {
      const adServices = getServicesByCategory('ad_platforms', clientType);
      selectedAdPlatforms.forEach((platform, index) => {
        const ad = adServices.find(s => s.tier_key === platform);
        if (ad) {
          const price = index === 0 ? ad.price : (ad.additional_price || ad.price);
          breakdown.push({
            category: 'ad_platforms',
            tier_key: ad.tier_key,
            price: price,
            name: ad.name
          });
          selectedKeys.push(ad.tier_key);
        }
      });

      const adSpendFee = adSpend * 0.10;
      breakdown.push({
        category: 'ad_platforms',
        tier_key: 'ad_spend_management',
        price: adSpendFee,
        name: 'Ad Spend Management (10%)'
      });
    }

    if (clientType === 'ecommerce' && croTier) {
      const cro = getServicesByCategory('cro_services').find(s => s.tier_key === croTier);
      if (cro) {
        breakdown.push({
          category: 'cro_services',
          tier_key: cro.tier_key,
          price: cro.price,
          name: cro.name
        });
        selectedKeys.push(cro.tier_key);
      }
    }

    if (multiLocation > 0) {
      const multiLocationPrice = multiLocation * getAdditionalPrice('multi_location');
      breakdown.push({
        category: 'seo_services',
        tier_key: 'multi_location',
        price: multiLocationPrice,
        name: 'Multi-Location'
      });
    }

    const monthly = breakdown.reduce((sum, item) => sum + item.price, 0);
    let disc = 0;
    let setupWaived = false;

    if (appliedDiscount) {
      const discountResult = calculateMarketingDiscount(
        appliedDiscount,
        breakdown,
        selectedKeys,
        selectedAdPlatforms.length
      );
      disc = discountResult.totalDiscount;
      setupWaived = discountResult.setupWaived;
    }

    const hasAds = selectedAdPlatforms.length > 0;
    const setupFee = getAdditionalPrice('setup_fee');
    const setup = (setupWaived && hasAds) ? 0 : (hasAds ? setupFee : 0);

    return { monthly: monthly - disc, setup, first: (monthly - disc) + setup, disc };
  };

  const renderStructuredFeatures = (features: any) => {
    if (!features) return null;

    // Handle structured features with sections property
    if (features.sections && Array.isArray(features.sections)) {
      return (
        <div className="space-y-6">
          {features.sections.map((section: any, sectionIdx: number) => (
            <div key={sectionIdx}>
              {section.title && (
                <h5 className="font-semibold text-sm text-gray-900 mb-2">{section.title}</h5>
              )}
              {section.subtitle && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.subtitle}</p>
              )}
              {section.description && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.description}</p>
              )}
              {section.items && section.items.length > 0 && (
                <ul className="space-y-1.5">
                  {section.items.map((item: string, itemIdx: number) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle array of section objects (stored as direct array)
    if (Array.isArray(features) && features.length > 0 && typeof features[0] === 'object' && features[0].title) {
      return (
        <div className="space-y-6">
          {features.map((section: any, sectionIdx: number) => (
            <div key={sectionIdx}>
              {section.title && (
                <h5 className="font-semibold text-sm text-gray-900 mb-2">{section.title}</h5>
              )}
              {section.subtitle && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.subtitle}</p>
              )}
              {section.description && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.description}</p>
              )}
              {section.items && section.items.length > 0 && (
                <ul className="space-y-1.5">
                  {section.items.map((item: string, itemIdx: number) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle simple array of strings (legacy format)
    if (Array.isArray(features)) {
      return (
        <ul className="space-y-1.5">
          {features.map((feature: string, idx: number) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      );
    }

    return null;
  };

  const renderFeaturesToPDF = (doc: any, features: any, startY: number, maxWidth: number = 153): number => {
    let y = startY;

    if (!features) return y;

    if (features.sections && Array.isArray(features.sections)) {
      features.sections.forEach((section: any) => {
        if (y > 270) { doc.addPage(); y = 20; }

        if (section.title) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(70, 70, 70);
          doc.text(section.title, 27, y);
          y += 3.5;
        }

        if (section.description) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(100, 100, 100);
          const descLines = doc.splitTextToSize(section.description, maxWidth);
          doc.text(descLines, 27, y);
          y += descLines.length * 3 + 1;
        }

        if (section.items && Array.isArray(section.items)) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(100, 100, 100);
          section.items.forEach((item: string) => {
            if (y > 270) { doc.addPage(); y = 20; }
            const itemLines = doc.splitTextToSize(`• ${item}`, maxWidth - 5);
            doc.text(itemLines, 32, y);
            y += itemLines.length * 3;
          });
        }
        y += 2;
      });
    } else if (Array.isArray(features)) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      features.forEach((feature: any) => {
        if (y > 270) { doc.addPage(); y = 20; }
        if (typeof feature === 'string') {
          const featureLines = doc.splitTextToSize(`• ${feature}`, maxWidth);
          doc.text(featureLines, 27, y);
          y += featureLines.length * 3.5;
        }
      });
    }

    return y;
  };

  const getImageDataURL = async (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          try {
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
          } catch (e) {
            reject(e);
          }
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };
      img.onerror = (e) => reject(new Error('Failed to load image'));
      img.src = url;
    });
  };

  async function saveEstimateChanges() {
    if (!estimate) return;

    try {
      setIsSaving(true);
      setSaveMessage('');

      const config = {
        includeWeb,
        includeMarketing,
        pkg,
        seoTier,
        emailTier,
        selectedAdPlatforms,
        croTier,
        multiLocation,
        addOns,
        customPages,
        sameLayoutPages,
        extraRevisions,
        adSpend,
        selectedSupportPlan,
        webDiscount: appliedWebDiscount?.code || null,
        marketingDiscount: appliedDiscount?.code || null,
      };

      const webInfo = calcWeb();
      const marketingFees = calcMarketing();
      const totals = {
        web_total: webInfo.total,
        web_original: webInfo.originalTotal,
        web_discount: webInfo.discount,
        marketing_monthly: marketingFees.monthly,
        marketing_setup: marketingFees.setup,
        marketing_discount: marketingFees.disc,
        total_due: webInfo.total + marketingFees.first,
      };

      const { error } = await supabase
        .from('estimates')
        .update({
          client_name: clientName,
          client_type: clientType,
          config,
          totals,
          updated_at: new Date().toISOString(),
        })
        .eq('id', estimate.id);

      if (error) throw error;

      setSaveMessage('Changes saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving estimate:', error);
      setSaveMessage('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  async function applyDiscountCode() {
    if (!discountCode.trim() || !brand) return;

    try {
      setDiscountError('');
      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('brand_id', brand.id)
        .eq('code', discountCode.toUpperCase())
        .eq('active', true)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setDiscountError('Invalid discount code');
        return;
      }

      setAppliedDiscount(data);
      setDiscountError('');
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('Error validating discount code');
    }
  }

  function handleAcceptProposal() {
    console.log('Accept button clicked, showing contract view');
    window.scrollTo(0, 0);
    setShowContract(true);
  }

  async function handleRejectProposal(reason: string, details: string) {
    if (!estimate) return;

    try {
      const { error } = await supabase
        .from('proposal_responses')
        .insert({
          estimate_id: estimate.id,
          status: 'rejected',
          rejection_reason: reason,
          rejection_details: details
        });

      if (error) throw error;

      const { data: opportunity } = await supabase
        .from('opportunities')
        .select('id')
        .eq('estimate_id', estimate.id)
        .maybeSingle();

      if (opportunity) {
        await markOpportunityLost(opportunity.id, `${reason}: ${details}`);
      }

      setProposalStatus('rejected');
      setShowRejectionModal(false);
    } catch (error) {
      console.error('Error submitting rejection:', error);
      toast.error('There was an error submitting your feedback. Please try again.', 'Feedback Error');
    }
  }

  function handleContractSigned() {
    setProposalStatus('accepted');
    setShowContract(false);
  }

  async function exportPDF() {
    if (!brand) return;

    const doc = new jsPDF();
    const webInfo = calcWeb();
    const marketingFees = calcMarketing();

    const primaryRgb = hexToRgb(brand.primary_color);
    const secondaryRgb = hexToRgb(brand.secondary_color);
    const accentColor = PDF_TYPOGRAPHY.colors.accentColor;
    const darkTeal = PDF_TYPOGRAPHY.colors.darkTeal;
    const lightGray = PDF_TYPOGRAPHY.colors.lightGray;

    const useWhiteBackground = brand.name === 'Web Design Miami Company' || brand.name === 'West Palm Beach Web Design Company';

    if (!useWhiteBackground) {
      doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
      doc.rect(0, 0, 210, 45, 'F');
    }

    if (brand.logo_url) {
      try {
        const logoDataURL = await getImageDataURL(brand.logo_url);
        const logoY = useWhiteBackground ? 20 : 15;
        const logoHeight = useWhiteBackground ? 20 : 12;
        doc.addImage(logoDataURL, 'PNG', 15, logoY, 70, logoHeight);
      } catch (e) {
        if (useWhiteBackground) {
          doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
        } else {
          doc.setTextColor(255, 255, 255);
        }
        doc.setFontSize(28);
        doc.setFont('helvetica', 'bold');
        doc.text(brand.name, 20, useWhiteBackground ? 35 : 28);
      }
    } else {
      if (useWhiteBackground) {
        doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
      } else {
        doc.setTextColor(255, 255, 255);
      }
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text(brand.name, 20, useWhiteBackground ? 35 : 28);
    }

    let y = 58;

    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, PDF_TYPOGRAPHY.maxWidths.clientInfo, 36, 2, 2, 'F');

    doc.setDrawColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.setLineWidth(0.5);
    doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, PDF_TYPOGRAPHY.maxWidths.clientInfo, 36, 2, 2, 'D');

    setColor(doc, darkTeal);
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.clientInfoLabel);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT INFORMATION', 20, y + 7);

    setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.clientInfo);
    doc.setFont('helvetica', 'normal');
    doc.text(`${clientName || 'N/A'}`, 20, y + 13);
    doc.text(`${companyName || 'N/A'}`, 20, y + 18);
    doc.text(`${clientEmail || 'N/A'}`, 20, y + 23);
    doc.text(`${new Date().toLocaleDateString()}`, 20, y + 28);
    doc.text(`${clientType === 'b2b' ? 'B2B' : clientType.charAt(0).toUpperCase() + clientType.slice(1)}`, 20, y + 33);

    y += 44;

    y = renderPageTitle(doc, 'INVESTMENT SUMMARY', y, primaryRgb);

    const hasSetupFeeWaived = appliedDiscount && appliedDiscount.waive_setup && selectedAdPlatforms.length > 0;
    const setupFeeSavings = hasSetupFeeWaived ? getAdditionalPrice('setup_fee') : 0;
    const hasWebDiscount = webInfo.discount > 0;
    const hasDiscounts = (marketingFees.disc > 0) || hasSetupFeeWaived || hasWebDiscount;

    let summaryHeight = 8;

    if (includeWeb) {
      summaryHeight += hasWebDiscount ? 11 : 6;
    }

    if (includeMarketing) {
      summaryHeight += marketingFees.disc > 0 ? 11 : 6;

      if (marketingFees.setup > 0 || hasSetupFeeWaived) {
        summaryHeight += hasSetupFeeWaived ? 11 : 6;
      }

      if (hasDiscounts) {
        summaryHeight += 4;
        summaryHeight += 5;
        if (hasWebDiscount) summaryHeight += 6;
        if (marketingFees.disc > 0) summaryHeight += 6;
        if (hasSetupFeeWaived) summaryHeight += 6;
        summaryHeight += 6;
      }
    } else if (hasWebDiscount) {
      summaryHeight += 4;
      summaryHeight += 5;
      summaryHeight += 6;
      summaryHeight += 6;
    }

    summaryHeight += 4;

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(PDF_TYPOGRAPHY.colors.border.r, PDF_TYPOGRAPHY.colors.border.g, PDF_TYPOGRAPHY.colors.border.b);
    doc.setLineWidth(0.3);
    doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, 180, summaryHeight, 2, 2, 'FD');

    y += 8;

    if (includeWeb) {
      setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryItem);
      doc.setFont('helvetica', 'normal');
      doc.text('Website Services:', PDF_TYPOGRAPHY.margins.contentLeft, y);
      doc.setFont('helvetica', 'bold');

      if (hasWebDiscount) {
        setColor(doc, PDF_TYPOGRAPHY.colors.strikethrough);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.strikethroughPrice);
        doc.setFont('helvetica', 'normal');
        doc.text(`${fmt(webInfo.originalTotal)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        doc.setDrawColor(PDF_TYPOGRAPHY.colors.strikethrough.r, PDF_TYPOGRAPHY.colors.strikethrough.g, PDF_TYPOGRAPHY.colors.strikethrough.b);
        doc.setLineWidth(0.5);
        const strikeWidth = doc.getTextWidth(`${fmt(webInfo.originalTotal)}`);
        doc.line(PDF_TYPOGRAPHY.margins.contentRight - strikeWidth, y - 1.5, PDF_TYPOGRAPHY.margins.contentRight, y - 1.5);
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
        y += 6;

        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.setFont('helvetica', 'bold');
        setColor(doc, PDF_TYPOGRAPHY.colors.green);
        doc.text(`${fmt(webInfo.total)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      } else {
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.text(`${fmt(webInfo.total)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      }
      y += 7;
    }

    if (hasWebDiscount || marketingFees.disc > 0 || hasSetupFeeWaived) {
      y += 4;

      setColor(doc, PDF_TYPOGRAPHY.colors.green);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.savingsLabel);
      doc.setFont('helvetica', 'bold');
      doc.text('SAVINGS:', PDF_TYPOGRAPHY.margins.contentLeft, y);
      y += 5;

      if (hasWebDiscount) {
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
        doc.setFont('helvetica', 'normal');
        const discountLabel = appliedWebDiscount?.code ? `Website Discount (${appliedWebDiscount.code}):` : 'Website Discount:';
        doc.text(discountLabel, PDF_TYPOGRAPHY.margins.indent, y);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
        doc.setFont('helvetica', 'bold');
        doc.text(`-${fmt(webInfo.discount)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        y += 6;
      }

      if (marketingFees.disc > 0) {
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
        doc.setFont('helvetica', 'normal');
        const discountLabel = appliedDiscount?.code ? `Marketing Discount (${appliedDiscount.code}):` : 'Marketing Discount:';
        doc.text(discountLabel, PDF_TYPOGRAPHY.margins.indent, y);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
        doc.setFont('helvetica', 'bold');
        doc.text(`-${fmt(marketingFees.disc)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        y += 6;
      }

      if (hasSetupFeeWaived) {
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
        doc.setFont('helvetica', 'normal');
        doc.text(`Setup Fee Waived:`, PDF_TYPOGRAPHY.margins.indent, y);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
        doc.setFont('helvetica', 'bold');
        doc.text(`-${fmt(setupFeeSavings)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        y += 6;
      }

      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
      doc.setFont('helvetica', 'bold');
      const totalSavings = webInfo.discount + marketingFees.disc + setupFeeSavings;
      doc.text(`Total Savings:`, PDF_TYPOGRAPHY.margins.indent, y);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
      doc.text(`-${fmt(totalSavings)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      y += 6;

      setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
    }

    if (includeMarketing) {
      const monthlyBeforeDiscount = marketingFees.monthly + marketingFees.disc;

      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryItem);
      doc.setFont('helvetica', 'normal');
      doc.text('Monthly Retainer:', PDF_TYPOGRAPHY.margins.contentLeft, y);
      doc.setFont('helvetica', 'bold');

      if (marketingFees.disc > 0) {
        setColor(doc, PDF_TYPOGRAPHY.colors.strikethrough);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.strikethroughPrice);
        doc.setFont('helvetica', 'normal');
        doc.text(`${fmt(monthlyBeforeDiscount)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        doc.setDrawColor(PDF_TYPOGRAPHY.colors.strikethrough.r, PDF_TYPOGRAPHY.colors.strikethrough.g, PDF_TYPOGRAPHY.colors.strikethrough.b);
        doc.setLineWidth(0.5);
        const strikeWidth = doc.getTextWidth(`${fmt(monthlyBeforeDiscount)}/mo`);
        doc.line(PDF_TYPOGRAPHY.margins.contentRight - strikeWidth, y - 1.5, PDF_TYPOGRAPHY.margins.contentRight, y - 1.5);
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
        y += 6;

        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.setFont('helvetica', 'bold');
        setColor(doc, PDF_TYPOGRAPHY.colors.green);
        doc.text(`${fmt(marketingFees.monthly)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      } else {
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.setFont('helvetica', 'bold');
        doc.text(`${fmt(marketingFees.monthly)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      }
      y += 7;

      if (marketingFees.setup > 0 || hasSetupFeeWaived) {
        const setupFeeAmount = getAdditionalPrice('setup_fee');
        doc.setFont('helvetica', 'normal');
        doc.text('Setup Fee:', PDF_TYPOGRAPHY.margins.contentLeft, y);

        if (hasSetupFeeWaived) {
          setColor(doc, PDF_TYPOGRAPHY.colors.strikethrough);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.strikethroughPrice);
          doc.setFont('helvetica', 'normal');
          doc.text(`${fmt(setupFeeAmount)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          doc.setDrawColor(PDF_TYPOGRAPHY.colors.strikethrough.r, PDF_TYPOGRAPHY.colors.strikethrough.g, PDF_TYPOGRAPHY.colors.strikethrough.b);
          doc.setLineWidth(0.5);
          const strikeWidth = doc.getTextWidth(`${fmt(setupFeeAmount)}`);
          doc.line(PDF_TYPOGRAPHY.margins.contentRight - strikeWidth, y - 1.5, PDF_TYPOGRAPHY.margins.contentRight, y - 1.5);
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          y += 6;

          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.green);
          doc.text(`$0`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
        } else {
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
          doc.setFont('helvetica', 'bold');
          doc.text(`${fmt(marketingFees.setup)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        }
        y += 7;
      }

      if (hasDiscounts) {
        y += 4;

        setColor(doc, PDF_TYPOGRAPHY.colors.green);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.savingsLabel);
        doc.setFont('helvetica', 'bold');
        doc.text('SAVINGS:', PDF_TYPOGRAPHY.margins.contentLeft, y);
        y += 5;

        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
        if (hasWebDiscount) {
          doc.setFont('helvetica', 'normal');
          doc.text(`Website Discount:`, PDF_TYPOGRAPHY.margins.indent, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
          doc.setFont('helvetica', 'bold');
          doc.text(`-${fmt(webInfo.discount)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          y += 6;
        }

        if (marketingFees.disc > 0) {
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
          doc.setFont('helvetica', 'normal');
          doc.text(`Monthly Discount:`, PDF_TYPOGRAPHY.margins.indent, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
          doc.setFont('helvetica', 'bold');
          doc.text(`-${fmt(marketingFees.disc)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          y += 6;
        }

        if (hasSetupFeeWaived) {
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
          doc.setFont('helvetica', 'normal');
          doc.text(`Setup Fee Waived:`, PDF_TYPOGRAPHY.margins.indent, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
          doc.setFont('helvetica', 'bold');
          doc.text(`-${fmt(setupFeeSavings)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          y += 6;
        }

        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
        doc.setFont('helvetica', 'bold');
        doc.text(`Total Savings:`, PDF_TYPOGRAPHY.margins.indent, y);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
        const oneTimeSavings = (hasWebDiscount ? webInfo.discount : 0) + setupFeeSavings;
        const monthlySavings = marketingFees.disc;
        let savingsText = '';
        if (oneTimeSavings > 0 && monthlySavings > 0) {
          savingsText = `-${fmt(oneTimeSavings)} + -${fmt(monthlySavings)}/mo`;
        } else if (oneTimeSavings > 0) {
          savingsText = `-${fmt(oneTimeSavings)}`;
        } else if (monthlySavings > 0) {
          savingsText = `-${fmt(monthlySavings)}/mo`;
        }
        doc.text(savingsText, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        y += 6;

        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      }
    }

    y += 6;

    doc.setFillColor(accentColor.r, accentColor.g, accentColor.b);
    doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, 180, 20, 2, 2, 'F');

    setColor(doc, darkTeal);
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.totalDueNow);
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL DUE NOW:`, PDF_TYPOGRAPHY.margins.contentLeft, y + 12);
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.totalDueNowAmount);
    doc.text(`${fmt(webInfo.total + marketingFees.first)}`, PDF_TYPOGRAPHY.margins.contentRight, y + 12, { align: 'right' });

    y += 24;

    if (includeMarketing && marketingFees.monthly > 0) {
      setColor(doc, PDF_TYPOGRAPHY.colors.textMuted);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.thenMonthly);
      doc.setFont('helvetica', 'italic');
      doc.text(`Then ${fmt(marketingFees.monthly + adSpend)}/month thereafter`, PDF_TYPOGRAPHY.margins.page, y);
      y += 6;
    }

    y = checkPageBreak(doc, y, 40);
    if (y === 20) {
      y = 20;
    } else {
      y += PDF_TYPOGRAPHY.spacing.subsection;
    }

    y = renderPageTitle(doc, 'DETAILED BREAKDOWN', y, primaryRgb);

    setColor(doc, PDF_TYPOGRAPHY.colors.textPrimary);
    doc.setFont('helvetica', 'normal');

    if (includeWeb) {
      y = renderSectionHeader(doc, 'Website Package', y, primaryRgb);

      const pkgService = getServicesByCategory('website_packages').find(s => s.tier_key === pkg);
      if (pkgService) {
        y = renderServiceItem(
          doc,
          pkgService.name,
          fmt(pkgService.price),
          y,
          pkgService.description,
          null,
          null
        );

        if (pkgService.payment_schedule && pkgService.payment_schedule.length > 0) {
          y = checkPageBreak(doc, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          doc.text('Payment Schedule:', PDF_TYPOGRAPHY.margins.contentLeft + 3, y);
          doc.setFont('helvetica', 'normal');
          y += PDF_TYPOGRAPHY.spacing.itemGap;
          pkgService.payment_schedule.forEach(milestone => {
            doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
            doc.text(`  ${milestone.percentage}%  ${milestone.description}`, 30, y);
            y += PDF_TYPOGRAPHY.lineHeights.list;
          });
          y += PDF_TYPOGRAPHY.spacing.bullet;
        }

        if (pkgService.key_deliverables && pkgService.key_deliverables.length > 0) {
          y = checkPageBreak(doc, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          doc.text('Key Deliverables:', PDF_TYPOGRAPHY.margins.contentLeft + 3, y);
          doc.setFont('helvetica', 'normal');
          y += PDF_TYPOGRAPHY.spacing.itemGap;
          pkgService.key_deliverables.forEach(deliverable => {
            doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
            const split = doc.splitTextToSize(`• ${deliverable}`, PDF_TYPOGRAPHY.maxWidths.description);
            doc.text(split, 30, y);
            y += split.length * PDF_TYPOGRAPHY.lineHeights.list;
          });
          y += PDF_TYPOGRAPHY.spacing.bullet;
        }

        if (pkgService.features) {
          y = checkPageBreak(doc, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          doc.text('Features Included:', PDF_TYPOGRAPHY.margins.contentLeft + 3, y);
          doc.setFont('helvetica', 'normal');
          y += PDF_TYPOGRAPHY.spacing.itemGap;
          y = renderFeaturesToPDF(doc, pkgService.features, y, PDF_TYPOGRAPHY.maxWidths.description);
          y += PDF_TYPOGRAPHY.spacing.bullet;
        }

        y += PDF_TYPOGRAPHY.spacing.lineItem;
      }

      if (addOns.length > 0 || customPages > 0 || sameLayoutPages > 0 || extraRevisions > 0) {
        y += 6;
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
        doc.text('Additional Items:', 22, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        y += 5;

        addOns.forEach(id => {
          const addon = getServicesByCategory('web_addons').find(s => s.tier_key === id);
          if (addon) {
            if (y > 270) { doc.addPage(); y = 20; }
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(50, 50, 50);
            doc.text(`${addon.name}`, 27, y);
            doc.text(`${fmt(addon.price)}`, 185, y, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            y += 4.5;

            if (addon.description) {
              doc.setFontSize(8);
              doc.setTextColor(100, 100, 100);
              const descLines = doc.splitTextToSize(addon.description, 158);
              doc.text(descLines, 27, y);
              y += descLines.length * 3.5;
              doc.setTextColor(0, 0, 0);
            }

            if (addon.features) {
              y = renderFeaturesToPDF(doc, addon.features, y, 153);
              y += 1;
            }
          }
        });

        if (customPages > 0) {
          const customPagePrice = additionalPricing.find(p => p.item_key === 'custom_page')?.price || 0;
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          doc.text(`Custom Pages (${customPages} x ${fmt(customPagePrice)})`, 27, y);
          doc.text(`${fmt(customPages * customPagePrice)}`, 185, y, { align: 'right' });
          y += 4.5;
          doc.setTextColor(0, 0, 0);
        }

        if (sameLayoutPages > 0) {
          const sameLayoutPrice = additionalPricing.find(p => p.item_key === 'same_layout_page')?.price || 0;
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          doc.text(`Same Layout Pages (${sameLayoutPages} x ${fmt(sameLayoutPrice)})`, 27, y);
          doc.text(`${fmt(sameLayoutPages * sameLayoutPrice)}`, 185, y, { align: 'right' });
          y += 4.5;
          doc.setTextColor(0, 0, 0);
        }

        if (extraRevisions > 0) {
          const revisionPrice = additionalPricing.find(p => p.item_key === 'extra_revision')?.price || 0;
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          doc.text(`Extra Revisions (${extraRevisions} x ${fmt(revisionPrice)})`, 27, y);
          doc.text(`${fmt(extraRevisions * revisionPrice)}`, 185, y, { align: 'right' });
          y += 4.5;
          doc.setTextColor(0, 0, 0);
        }
      }

      y += 4;
      doc.setDrawColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
      doc.setLineWidth(0.5);
      doc.line(20, y, 190, y);
      y += 6;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
      doc.text(`Website Total:`, 22, y);
      doc.text(`${fmt(webInfo.total)}`, 185, y, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      y += 10;
    }

    if (includeMarketing) {
      if (y > 230) { doc.addPage(); y = 20; }

      doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
      doc.roundedRect(15, y, 180, 8, 1, 1, 'F');

      doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
      doc.rect(15, y, 3, 8, 'F');

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
      doc.text('Marketing Services', 22, y + 5.5);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      y += 12;

      doc.setFontSize(10);

      if (selectedSupportPlan) {
        const plan = supportPlans.find(p => p.tier_key === selectedSupportPlan);
        if (plan) {
          if (y > 250) { doc.addPage(); y = 20; }
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 50, 50);
          doc.text(`${plan.name}`, 22, y);
          doc.text(`${fmt(plan.price)}/mo`, 185, y, { align: 'right' });
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(80, 80, 80);
          y += 5;

          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(`${plan.hours} hours/month`, 27, y);
          y += 4;

          if (plan.description) {
            const descLines = doc.splitTextToSize(plan.description, 158);
            doc.text(descLines, 27, y);
            y += descLines.length * 3.5 + 1;
          }

          if (plan.features) {
            y = renderFeaturesToPDF(doc, plan.features, y, 153);
            y += 2;
          }
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
        }
      }

      if (seoTier) {
        const seo = getServicesByCategory('seo_services', clientType).find(s => s.tier_key === seoTier);
        if (seo) {
          if (y > 250) { doc.addPage(); y = 20; }
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 50, 50);
          doc.text(`${seo.name}`, 22, y);
          doc.text(`${fmt(seo.price)}/mo`, 185, y, { align: 'right' });
          doc.setFont('helvetica', 'normal');
          y += 5;

          if (seo.description) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const descLines = doc.splitTextToSize(seo.description, 158);
            doc.text(descLines, 27, y);
            y += descLines.length * 3.5 + 1;
          }

          if (seo.features) {
            y = renderFeaturesToPDF(doc, seo.features, y, 153);
            y += 2;
          }
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
        }
      }

      if (emailTier) {
        const email = getServicesByCategory('email_services', clientType).find(s => s.tier_key === emailTier);
        if (email) {
          if (y > 250) { doc.addPage(); y = 20; }
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 50, 50);
          doc.text(`${email.name}`, 22, y);
          doc.text(`${fmt(email.price)}/mo`, 185, y, { align: 'right' });
          doc.setFont('helvetica', 'normal');
          y += 5;

          if (email.description) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const descLines = doc.splitTextToSize(email.description, 158);
            doc.text(descLines, 27, y);
            y += descLines.length * 3.5 + 1;
          }

          if (email.features) {
            y = renderFeaturesToPDF(doc, email.features, y, 153);
            y += 2;
          }
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
        }
      }

      if (selectedAdPlatforms.length > 0) {
        const adServices = getServicesByCategory('ad_platforms', clientType);
        selectedAdPlatforms.forEach((platform, index) => {
          const ad = adServices.find(s => s.tier_key === platform);
          if (ad) {
            if (y > 250) { doc.addPage(); y = 20; }
            const adPrice = index === 0 ? ad.price : (ad.additional_price || ad.price);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(50, 50, 50);
            doc.text(`${ad.name}`, 22, y);
            doc.text(`${fmt(adPrice)}/mo`, 185, y, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            y += 5;

            if (ad.description) {
              doc.setFontSize(8);
              doc.setTextColor(100, 100, 100);
              const descLines = doc.splitTextToSize(ad.description, 158);
              doc.text(descLines, 27, y);
              y += descLines.length * 3.5 + 1;
            }

            if (ad.features) {
              y = renderFeaturesToPDF(doc, ad.features, y, 153);
              y += 2;
            }
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
          }
        });

        if (y > 270) { doc.addPage(); y = 20; }
        const adManagementFee = adSpend * 0.10;
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.setFont('helvetica', 'normal');
        doc.text(`Ad Management (10% of ${fmt(adSpend)})`, 27, y);
        doc.text(`${fmt(adManagementFee)}/mo`, 185, y, { align: 'right' });
        y += 4.5;
        doc.text(`Ad Spend Budget`, 27, y);
        doc.text(`${fmt(adSpend)}/mo`, 185, y, { align: 'right' });
        y += 8;
        doc.setTextColor(0, 0, 0);
      }

      if (clientType === 'ecommerce' && croTier) {
        const cro = getServicesByCategory('cro_services').find(s => s.tier_key === croTier);
        if (cro) {
          if (y > 250) { doc.addPage(); y = 20; }
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 50, 50);
          doc.text(`${cro.name}`, 22, y);
          doc.text(`${fmt(cro.price)}/mo`, 185, y, { align: 'right' });
          doc.setFont('helvetica', 'normal');
          y += 5;

          if (cro.description) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const descLines = doc.splitTextToSize(cro.description, 158);
            doc.text(descLines, 27, y);
            y += descLines.length * 3.5 + 1;
          }

          if (cro.features) {
            y = renderFeaturesToPDF(doc, cro.features, y, 153);
            y += 2;
          }
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
        }
      }

      if (multiLocation > 0) {
        const multiLocationPrice = additionalPricing.find(p => p.item_key === 'multi_location')?.price || 0;
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(`Multi-Location (${multiLocation} x ${fmt(multiLocationPrice)})`, 22, y);
        doc.text(`${fmt(multiLocation * multiLocationPrice)}/mo`, 185, y, { align: 'right' });
        y += 5;
        doc.setTextColor(0, 0, 0);
      }
    }

    if (brand.terms_text) {
      if (y > 260) {
        doc.addPage();
        y = 20;
      } else {
        y += 10;
      }
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      const termsLines = doc.splitTextToSize(brand.terms_text, 180);
      doc.text(termsLines, 15, y);
    }

    const filename = `${clientName.replace(/[^a-z0-9]/gi, '_')}-${brand.name.replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  }

  const webInfo = calcWeb();
  const marketingFees = calcMarketing();

  const packages = getServicesByCategory('website_packages');
  const webAddOns = getServicesByCategory('web_addons');
  const seoServices = getServicesByCategory('seo_services', clientType);
  const emailServices = getServicesByCategory('email_services', clientType);
  const adPlatforms = getServicesByCategory('ad_platforms', clientType);
  const croServices = getServicesByCategory('cro_services');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-lg font-semibold text-gray-700">Loading your proposal...</div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-lg font-semibold text-gray-700">Proposal not found</div>
      </div>
    );
  }

  const brandGradient = `linear-gradient(135deg, ${brand.primary_color} 0%, ${brand.secondary_color} 100%)`;
  const brandGradientSubtle = `linear-gradient(135deg, ${brand.primary_color}15 0%, ${brand.secondary_color}15 100%)`;

  if (showContract && estimate && brand) {
    console.log('Rendering ContractView', { estimate: estimate.id, brand: brand.id });
    return (
      <ContractView
        estimate={estimate}
        brand={brand}
        onBack={() => setShowContract(false)}
        onContractSigned={handleContractSigned}
      />
    );
  }

  return (
    <>
      <RejectionModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onSubmit={handleRejectProposal}
      />
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div
        className="w-full py-8 px-4 sm:px-6 mb-6 shadow-lg"
        style={{
          background: (brand.name === 'Web Design Miami Company' || brand.name === 'West Palm Beach Web Design Company')
            ? 'white'
            : brandGradient
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            {brand.logo_url && (
              <img
                src={brand.logo_url}
                alt={brand.name}
                className="h-12 sm:h-16 object-contain"
              />
            )}
            <div className="text-right">
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight"
                style={{
                  color: (brand.name === 'Web Design Miami Company' || brand.name === 'West Palm Beach Web Design Company')
                    ? '#000000'
                    : '#ffffff'
                }}
              >
                {clientName ? `Proposal for ${clientName}` : 'Service Proposal'}
              </h1>
              {estimate && (
                <p
                  className="text-sm mt-1"
                  style={{
                    color: (brand.name === 'Web Design Miami Company' || brand.name === 'West Palm Beach Web Design Company')
                      ? '#000000'
                      : '#ffffff',
                    opacity: (brand.name === 'Web Design Miami Company' || brand.name === 'West Palm Beach Web Design Company')
                      ? 1
                      : 0.9
                  }}
                >
                  Created {new Date(estimate.created_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-gray-800">Client Type</h2>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {(['local', 'ecommerce', 'b2b'] as const).map(t => {
              const isSelected = clientType === t;
              return (
                <button
                  key={t}
                  onClick={() => setClientType(t)}
                  className="p-3 sm:p-5 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md"
                  style={isSelected ? {
                    borderColor: brand.primary_color,
                    background: brandGradientSubtle,
                    opacity: 1
                  } : {
                    borderColor: '#e5e7eb',
                    opacity: 0.4
                  }}
                >
                  {t === 'local' && (
                    <Building2
                      className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2"
                      style={{ color: isSelected ? brand.primary_color : '#6b7280' }}
                    />
                  )}
                  {t === 'ecommerce' && (
                    <ShoppingCart
                      className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2"
                      style={{ color: isSelected ? brand.primary_color : '#6b7280' }}
                    />
                  )}
                  {t === 'b2b' && (
                    <Briefcase
                      className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2"
                      style={{ color: isSelected ? brand.primary_color : '#6b7280' }}
                    />
                  )}
                  <div
                    className="font-bold text-xs sm:text-base capitalize"
                    style={{ color: isSelected ? brand.primary_color : '#374151' }}
                  >
                    {t === 'b2b' ? 'B2B' : t}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-gray-800">Services</h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
            <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-all">
              <input
                type="checkbox"
                checked={includeWeb}
                onChange={(e) => setIncludeWeb(e.target.checked)}
                className="w-5 h-5 accent-blue-600"
              />
              <span className="font-semibold text-gray-700">Website Services</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-all">
              <input
                type="checkbox"
                checked={includeMarketing}
                onChange={(e) => setIncludeMarketing(e.target.checked)}
                className="w-5 h-5 accent-blue-600"
              />
              <span className="font-semibold text-gray-700">Marketing Services</span>
            </label>
          </div>

          {includeWeb && (
            <div className="mb-6 pb-6 border-b">
              <h3 className="font-bold text-base sm:text-lg mb-4 text-gray-800">Website Packages</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {packages.map(p => {
                  const isSelected = pkg === p.tier_key;
                  return (
                    <div key={p.id} className="flex flex-col">
                      <button
                        onClick={() => setPkg(p.tier_key)}
                        className="p-4 sm:p-5 rounded-xl border-2 transition-all transform hover:scale-105 hover:shadow-lg text-left"
                        style={isSelected ? {
                          borderColor: brand.primary_color,
                          background: brandGradientSubtle,
                          boxShadow: `0 4px 12px ${brand.primary_color}20`
                        } : {
                          borderColor: '#e5e7eb'
                        }}
                      >
                        <div className="font-bold text-base sm:text-lg mb-2 text-gray-800">{p.name}</div>
                        {p.show_description && p.description && (
                          <div className="text-xs sm:text-sm text-gray-600 mb-3 leading-relaxed">{p.description}</div>
                        )}
                        <div
                          className="text-xl sm:text-2xl lg:text-3xl font-bold"
                          style={{ color: brand.primary_color }}
                        >
                          {fmt(p.price)}
                        </div>
                      </button>
                      {isSelected && (() => {
                        const hasFeatures = p.features && (
                          (p.features.sections && Array.isArray(p.features.sections) && p.features.sections.length > 0) ||
                          (Array.isArray(p.features) && p.features.length > 0 && (
                            typeof p.features[0] === 'string' ||
                            (typeof p.features[0] === 'object' && p.features[0] !== null)
                          ))
                        );
                        const hasDeliverables = p.key_deliverables && p.key_deliverables.length > 0;

                        if (!hasFeatures && !hasDeliverables) return null;

                        return (
                          <div className="mt-2">
                            <button
                              onClick={() => setExpandedPackage(expandedPackage === p.tier_key ? null : p.tier_key)}
                              className="w-full px-4 py-3 rounded-xl flex justify-between items-center text-sm font-semibold transition-all"
                              style={{
                                background: brandGradientSubtle,
                                color: brand.primary_color
                              }}
                            >
                              <span>View Details</span>
                              {expandedPackage === p.tier_key ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expandedPackage === p.tier_key && (
                              <div className="mt-2 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                                {hasFeatures && (
                                  <div className={hasDeliverables ? "mb-4" : ""}>
                                    <h5 className="font-bold text-sm mb-3 text-gray-800">Features Included:</h5>
                                    {renderStructuredFeatures(p.features)}
                                  </div>
                                )}
                                {hasDeliverables && (
                                  <div>
                                    <h5 className="font-bold text-sm mb-3 text-gray-800">Key Deliverables:</h5>
                                    <ul className="space-y-1">
                                      {p.key_deliverables.map((deliverable, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm">
                                          <Check className="w-4 h-4 text-gray-700 mt-0.5 flex-shrink-0" />
                                          <span>{deliverable}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>

              {webAddOns.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-bold text-base sm:text-lg mb-4 text-gray-800">Add-Ons</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {webAddOns.map(addon => (
                      <label key={addon.id} className="flex items-center gap-3 p-4 border-2 rounded-xl hover:shadow-md cursor-pointer transition-all">
                        <input
                          type="checkbox"
                          checked={addOns.includes(addon.tier_key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAddOns([...addOns, addon.tier_key]);
                            } else {
                              setAddOns(addOns.filter(a => a !== addon.tier_key));
                            }
                          }}
                          className="w-5 h-5 accent-blue-600"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">{addon.name}</div>
                          <div className="text-sm font-bold mt-1" style={{ color: brand.primary_color }}>{fmt(addon.price)}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">Custom Pages</label>
                      <input
                        type="number"
                        value={customPages}
                        onChange={(e) => setCustomPages(parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-all outline-none"
                        min="0"
                      />
                      {customPages > 0 && (
                        <p className="text-xs text-gray-600 mt-1">
                          {fmt(customPages * getAdditionalPrice('custom_page'))}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">Same Layout Pages</label>
                      <input
                        type="number"
                        value={sameLayoutPages}
                        onChange={(e) => setSameLayoutPages(parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-all outline-none"
                        min="0"
                      />
                      {sameLayoutPages > 0 && (
                        <p className="text-xs text-gray-600 mt-1">
                          {fmt(sameLayoutPages * getAdditionalPrice('same_layout_page'))}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">Extra Revisions</label>
                      <input
                        type="number"
                        value={extraRevisions}
                        onChange={(e) => setExtraRevisions(parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-all outline-none"
                        min="0"
                      />
                      {extraRevisions > 0 && (
                        <p className="text-xs text-gray-600 mt-1">
                          {fmt(extraRevisions * getAdditionalPrice('extra_revision'))}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {includeMarketing && (
            <div>
              <h3 className="font-bold text-base sm:text-lg mb-4 text-gray-800">Marketing Services</h3>

              {supportPlans.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Website Care & Development Plans</label>
                  <CustomSelect
                    options={[
                      { value: '', label: 'None' },
                      ...supportPlans.map(plan => ({
                        value: plan.tier_key,
                        label: `${plan.name} - ${plan.hours}hrs/mo - ${fmt(plan.price)}/mo`
                      }))
                    ]}
                    value={selectedSupportPlan || ''}
                    onChange={(value) => setSelectedSupportPlan(value || null)}
                    className="w-full bg-white border-2 border-gray-200 text-gray-900 hover:border-gray-300 focus:ring-gray-400"
                    dropdownClassName="animate-scale-in"
                  />
                  {selectedSupportPlan && (() => {
                    const plan = supportPlans.find(p => p.tier_key === selectedSupportPlan);
                    const hasFeatures = plan?.features && (
                      (plan.features.sections && Array.isArray(plan.features.sections) && plan.features.sections.length > 0) ||
                      (Array.isArray(plan.features) && plan.features.length > 0 && (
                        typeof plan.features[0] === 'string' ||
                        (typeof plan.features[0] === 'object' && plan.features[0] !== null)
                      ))
                    );
                    if (!plan || (!plan.description && !hasFeatures)) return null;
                    return (
                      <div className="mt-2">
                        <button
                          onClick={() => setExpandedService(expandedService === `support-${selectedSupportPlan}` ? null : `support-${selectedSupportPlan}`)}
                          className="w-full px-4 py-3 rounded-xl flex justify-between items-center text-sm font-semibold transition-all"
                          style={{
                            background: brandGradientSubtle,
                            color: brand.primary_color
                          }}
                        >
                          <span>View Details</span>
                          {expandedService === `support-${selectedSupportPlan}` ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        {expandedService === `support-${selectedSupportPlan}` && (
                          <div className="mt-2 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                            {plan.description && (
                              <p className="text-sm text-gray-600 mb-3 leading-relaxed">{plan.description}</p>
                            )}
                            {hasFeatures && (
                              <div>
                                <h5 className="font-bold text-sm mb-3 text-gray-800">Features Included:</h5>
                                {renderStructuredFeatures(plan.features)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {seoServices.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">SEO Services</label>
                  <CustomSelect
                    options={[
                      { value: '', label: 'None' },
                      ...seoServices.map(s => ({
                        value: s.tier_key,
                        label: `${s.name} - ${fmt(s.price)}/mo`
                      }))
                    ]}
                    value={seoTier || ''}
                    onChange={(value) => setSeoTier(value || null)}
                    className="w-full bg-white border-2 border-gray-200 text-gray-900 hover:border-gray-300 focus:ring-gray-400"
                    dropdownClassName="animate-scale-in"
                  />
                  {seoTier && (() => {
                    const seo = seoServices.find(s => s.tier_key === seoTier);
                    if (!seo || (!seo.description && !seo.features)) return null;
                    return (
                      <div className="mt-2">
                        <button
                          onClick={() => setExpandedService(expandedService === `seo-${seoTier}` ? null : `seo-${seoTier}`)}
                          className="w-full px-4 py-3 rounded-xl flex justify-between items-center text-sm font-semibold transition-all"
                          style={{
                            background: brandGradientSubtle,
                            color: brand.primary_color
                          }}
                        >
                          <span>View Details</span>
                          {expandedService === `seo-${seoTier}` ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        {expandedService === `seo-${seoTier}` && (
                          <div className="mt-2 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                            {seo.description && (
                              <p className="text-sm text-gray-600 mb-3 leading-relaxed">{seo.description}</p>
                            )}
                            {seo.features && (
                              <div>
                                <h5 className="font-bold text-sm mb-3 text-gray-800">Features Included:</h5>
                                {renderStructuredFeatures(seo.features)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {emailServices.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Email & SMS Marketing</label>
                  <CustomSelect
                    options={[
                      { value: '', label: 'None' },
                      ...emailServices.map(s => ({
                        value: s.tier_key,
                        label: `${s.name} - ${fmt(s.price)}/mo`
                      }))
                    ]}
                    value={emailTier || ''}
                    onChange={(value) => setEmailTier(value || null)}
                    className="w-full bg-white border-2 border-gray-200 text-gray-900 hover:border-gray-300 focus:ring-gray-400"
                    dropdownClassName="animate-scale-in"
                  />
                  {emailTier && (() => {
                    const email = emailServices.find(s => s.tier_key === emailTier);
                    if (!email || (!email.description && !email.features)) return null;
                    return (
                      <div className="mt-2">
                        <button
                          onClick={() => setExpandedService(expandedService === `email-${emailTier}` ? null : `email-${emailTier}`)}
                          className="w-full px-4 py-3 rounded-xl flex justify-between items-center text-sm font-semibold transition-all"
                          style={{
                            background: brandGradientSubtle,
                            color: brand.primary_color
                          }}
                        >
                          <span>View Details</span>
                          {expandedService === `email-${emailTier}` ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        {expandedService === `email-${emailTier}` && (
                          <div className="mt-2 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                            {email.description && (
                              <p className="text-sm text-gray-600 mb-3 leading-relaxed">{email.description}</p>
                            )}
                            {email.features && (
                              <div>
                                <h5 className="font-bold text-sm mb-3 text-gray-800">Features Included:</h5>
                                {renderStructuredFeatures(email.features)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {adPlatforms.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Paid Ads</label>
                  <div className="space-y-2 mb-4">
                    {adPlatforms.map((ad, index) => {
                      const isSelected = selectedAdPlatforms.includes(ad.tier_key);
                      const price = index === 0 ? ad.price : (ad.additional_price || ad.price);
                      return (
                        <div key={ad.id}>
                          <label className="flex items-center gap-3 p-3 border-2 rounded-xl hover:shadow-md cursor-pointer transition-all">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAdPlatforms([...selectedAdPlatforms, ad.tier_key]);
                                } else {
                                  setSelectedAdPlatforms(selectedAdPlatforms.filter(p => p !== ad.tier_key));
                                }
                              }}
                              className="w-5 h-5 accent-blue-600"
                            />
                            <span className="font-semibold text-gray-800">{ad.name} - {fmt(price)}/mo</span>
                          </label>
                          {isSelected && (ad.description || ad.features) && (
                            <div className="ml-8 mt-2">
                              <button
                                onClick={() => setExpandedService(expandedService === `ad-${ad.tier_key}` ? null : `ad-${ad.tier_key}`)}
                                className="w-full px-4 py-2 rounded-xl flex justify-between items-center text-xs font-semibold transition-all"
                                style={{
                                  background: brandGradientSubtle,
                                  color: brand.primary_color
                                }}
                              >
                                <span>View Details</span>
                                {expandedService === `ad-${ad.tier_key}` ? (
                                  <ChevronUp className="w-3 h-3" />
                                ) : (
                                  <ChevronDown className="w-3 h-3" />
                                )}
                              </button>
                              {expandedService === `ad-${ad.tier_key}` && (
                                <div className="mt-2 p-3 bg-gray-50 rounded-xl border-2 border-gray-200">
                                  {ad.description && (
                                    <p className="text-xs text-gray-600 mb-2 leading-relaxed">{ad.description}</p>
                                  )}
                                  {ad.features && (
                                    <div>
                                      <h5 className="font-bold text-xs mb-2 text-gray-800">Features Included:</h5>
                                      {renderStructuredFeatures(ad.features)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedAdPlatforms.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold mb-3 text-gray-700">Monthly Ad Budget</label>
                      <input
                        type="number"
                        value={adSpend}
                        onChange={(e) => setAdSpend(parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-all outline-none font-semibold text-lg"
                        min="0"
                        step="500"
                      />
                      <p className="text-sm text-gray-600 mt-2 font-medium">10% management fee: {fmt(adSpend * 0.10)}/mo</p>
                    </div>
                  )}
                </div>
              )}

              {clientType === 'ecommerce' && croServices.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Conversion Rate Optimization</label>
                  <CustomSelect
                    options={[
                      { value: '', label: 'None' },
                      ...croServices.map(s => ({
                        value: s.tier_key,
                        label: `${s.name} - ${fmt(s.price)}/mo`
                      }))
                    ]}
                    value={croTier || ''}
                    onChange={(value) => setCroTier(value || null)}
                    className="w-full bg-white border-2 border-gray-200 text-gray-900 hover:border-gray-300 focus:ring-gray-400"
                    dropdownClassName="animate-scale-in"
                  />
                  {croTier && (() => {
                    const cro = croServices.find(s => s.tier_key === croTier);
                    if (!cro || (!cro.description && !cro.features)) return null;
                    return (
                      <div className="mt-2">
                        <button
                          onClick={() => setExpandedService(expandedService === `cro-${croTier}` ? null : `cro-${croTier}`)}
                          className="w-full px-4 py-3 rounded-xl flex justify-between items-center text-sm font-semibold transition-all"
                          style={{
                            background: brandGradientSubtle,
                            color: brand.primary_color
                          }}
                        >
                          <span>View Details</span>
                          {expandedService === `cro-${croTier}` ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        {expandedService === `cro-${croTier}` && (
                          <div className="mt-2 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                            {cro.description && (
                              <p className="text-sm text-gray-600 mb-3 leading-relaxed">{cro.description}</p>
                            )}
                            {cro.features && (
                              <div>
                                <h5 className="font-bold text-sm mb-3 text-gray-800">Features Included:</h5>
                                {renderStructuredFeatures(cro.features)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {clientType === 'local' && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Additional Locations</label>
                  <input
                    type="number"
                    value={multiLocation}
                    onChange={(e) => setMultiLocation(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-all outline-none font-semibold text-lg"
                    min="0"
                    step="1"
                  />
                  {multiLocation > 0 && (
                    <p className="text-sm text-gray-600 mt-2 font-medium">
                      {multiLocation} location{multiLocation > 1 ? 's' : ''} × {fmt(getAdditionalPrice('multi_location'))} = {fmt(multiLocation * getAdditionalPrice('multi_location'))}/mo
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="p-6 sm:p-8 rounded-2xl shadow-2xl"
          style={{ background: `linear-gradient(to bottom, white 0%, ${brand.primary_color}05 100%)` }}
        >
          <div
            className="text-2xl sm:text-3xl font-bold mb-6 pb-4 border-b-2"
            style={{
              borderImage: brandGradient,
              borderImageSlice: 1
            }}
          >
            <span style={{ background: brandGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Summary
            </span>
          </div>

          {includeWeb && (
            <div className="mb-6 pb-6 border-b-2 border-gray-100">
              <h3 className="text-base sm:text-lg font-bold mb-4 text-gray-700">Website Services</h3>

              <div className="mb-4 pb-4 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-600 mb-3">Website Breakdown</h4>
                <div className="space-y-2">
                  {(() => {
                    const pkgService = getServicesByCategory('website_packages').find(s => s.tier_key === pkg);
                    if (!pkgService) return null;
                    return (
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-600">{pkgService.name}</span>
                        <span className="font-semibold">{fmt(pkgService.price)}</span>
                      </div>
                    );
                  })()}

                  {addOns.map(addonKey => {
                    const addon = getServicesByCategory('web_addons').find(s => s.tier_key === addonKey);
                    if (!addon) return null;
                    return (
                      <div key={addonKey} className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-600">{addon.name}</span>
                        <span className="font-semibold">{fmt(addon.price)}</span>
                      </div>
                    );
                  })}

                  {customPages > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-gray-600">Custom Pages ({customPages})</span>
                      <span className="font-semibold">{fmt(customPages * getAdditionalPrice('custom_page'))}</span>
                    </div>
                  )}

                  {sameLayoutPages > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-gray-600">Same Layout Pages ({sameLayoutPages})</span>
                      <span className="font-semibold">{fmt(sameLayoutPages * getAdditionalPrice('same_layout_page'))}</span>
                    </div>
                  )}

                  {extraRevisions > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-gray-600">Extra Revisions ({extraRevisions})</span>
                      <span className="font-semibold">{fmt(extraRevisions * getAdditionalPrice('extra_revision'))}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-base sm:text-lg font-bold text-gray-700">Website Total</span>
                <span className="text-2xl sm:text-3xl lg:text-4xl font-bold" style={{ color: brand.primary_color }}>{fmt(webInfo.total)}</span>
              </div>
            </div>
          )}

          {includeMarketing && (
            <div className="mb-6 pb-6 border-b-2 border-gray-100">
              <h3 className="text-base sm:text-lg font-bold mb-4 text-gray-700">Marketing Services</h3>

              <div className="mb-4 pb-4 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-600 mb-3">Marketing Breakdown</h4>
                <div className="space-y-2">
                  {selectedSupportPlan && (() => {
                    const plan = supportPlans.find(p => p.tier_key === selectedSupportPlan);
                    if (!plan) return null;
                    return (
                      <div key="support" className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-600">{plan.name}</span>
                        <span className="font-semibold">{fmt(plan.price)}/mo</span>
                      </div>
                    );
                  })()}

                  {seoTier && (() => {
                    const seo = getServicesByCategory('seo_services', clientType).find(s => s.tier_key === seoTier);
                    if (!seo) return null;
                    return (
                      <div key="seo" className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-600">{seo.name}</span>
                        <span className="font-semibold">{fmt(seo.price)}/mo</span>
                      </div>
                    );
                  })()}

                  {emailTier && (() => {
                    const email = getServicesByCategory('email_services', clientType).find(s => s.tier_key === emailTier);
                    if (!email) return null;
                    return (
                      <div key="email" className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-600">{email.name}</span>
                        <span className="font-semibold">{fmt(email.price)}/mo</span>
                      </div>
                    );
                  })()}

                  {selectedAdPlatforms.map((platformKey, index) => {
                    const platform = adPlatforms.find(p => p.tier_key === platformKey);
                    if (!platform) return null;
                    const price = index === 0 ? platform.price : (platform.additional_price || platform.price);
                    return (
                      <div key={platformKey} className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-600">{platform.name}</span>
                        <span className="font-semibold">{fmt(price)}/mo</span>
                      </div>
                    );
                  })}

                  {selectedAdPlatforms.length > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-gray-600">Ad Management (10%)</span>
                      <span className="font-semibold">{fmt(adSpend * 0.10)}/mo</span>
                    </div>
                  )}

                  {croTier && (() => {
                    const cro = getServicesByCategory('cro_services').find(s => s.tier_key === croTier);
                    if (!cro) return null;
                    return (
                      <div key="cro" className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-600">{cro.name}</span>
                        <span className="font-semibold">{fmt(cro.price)}/mo</span>
                      </div>
                    );
                  })()}

                  {multiLocation > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-gray-600">Multi-Location ({multiLocation})</span>
                      <span className="font-semibold">{fmt(multiLocation * getAdditionalPrice('multi_location'))}/mo</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base font-semibold text-gray-600">Service Retainer</span>
                  <span className="text-xl sm:text-2xl font-bold" style={{ color: brand.primary_color }}>{fmt(marketingFees.monthly)}/mo</span>
                </div>

                {selectedAdPlatforms.length > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm sm:text-base font-semibold text-gray-600">Ad Spend Budget</span>
                    <span className="text-xl sm:text-2xl font-bold" style={{ color: brand.primary_color }}>{fmt(adSpend)}/mo</span>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm sm:text-base font-bold text-gray-700">Total Monthly Investment</span>
                  <span className="text-xl sm:text-2xl font-bold" style={{ color: brand.primary_color }}>{fmt(marketingFees.monthly + adSpend)}/mo</span>
                </div>

                {(marketingFees.setup > 0 || (appliedDiscount && appliedDiscount.waive_setup && selectedAdPlatforms.length > 0)) && (
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <span className="text-sm sm:text-base font-semibold text-gray-600">Setup Fee</span>
                    <span className="text-lg sm:text-xl font-bold text-gray-700">
                      {appliedDiscount && appliedDiscount.waive_setup && selectedAdPlatforms.length > 0 && (
                        <span className="line-through text-gray-400 mr-2">{fmt(getAdditionalPrice('setup_fee'))}</span>
                      )}
                      {fmt(marketingFees.setup)}
                    </span>
                  </div>
                )}
              </div>

              {(marketingFees.disc > 0 || (appliedDiscount && appliedDiscount.waive_setup && selectedAdPlatforms.length > 0)) && (
                <div className="mt-4 pt-4 border-t-2 border-gray-200 bg-green-50 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 mb-3">Savings Applied</h4>
                  {marketingFees.disc > 0 && (
                    <div className="flex justify-between items-baseline text-sm mb-2">
                      <span className="text-green-700">Discount on Services</span>
                      <span className="font-semibold text-green-700">-{fmt(marketingFees.disc)}/mo</span>
                    </div>
                  )}
                  {appliedDiscount && appliedDiscount.waive_setup && selectedAdPlatforms.length > 0 && (
                    <div className="flex justify-between items-baseline text-sm mb-2">
                      <span className="text-green-700">Setup Fee Waived</span>
                      <span className="font-semibold text-green-700">-{fmt(getAdditionalPrice('setup_fee'))}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t border-green-200">
                    <span className="font-semibold text-green-800">Total Savings</span>
                    <span className="font-bold text-lg text-green-800">
                      {marketingFees.disc > 0 && `-${fmt(marketingFees.disc)}/mo`}
                      {marketingFees.disc > 0 && appliedDiscount && appliedDiscount.waive_setup && selectedAdPlatforms.length > 0 && ' + '}
                      {appliedDiscount && appliedDiscount.waive_setup && selectedAdPlatforms.length > 0 && `-${fmt(getAdditionalPrice('setup_fee'))}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            className="p-4 sm:p-6 rounded-2xl mb-6"
            style={{ background: brandGradientSubtle }}
          >
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
              <span className="text-lg sm:text-xl font-bold text-gray-700">Total Investment</span>
              <span
                className="text-3xl sm:text-4xl lg:text-5xl font-bold"
                style={{
                  background: brandGradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {fmt(webInfo.total + marketingFees.first)}
              </span>
            </div>
          </div>

          {includeMarketing && marketingFees.monthly > 0 && (
            <div className="text-center text-base sm:text-lg mb-6 p-3 bg-gray-50 rounded-xl">
              Then <span className="font-bold text-lg sm:text-xl" style={{ color: brand.primary_color }}>{fmt(marketingFees.monthly + adSpend)}/month</span>
            </div>
          )}

          <div className="space-y-3">
            {estimate && (
              <div className="mb-4">
                <button
                  onClick={saveEstimateChanges}
                  disabled={isSaving}
                  className="w-full py-3 px-4 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-semibold"
                >
                  <Save className="w-5 h-5" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                {saveMessage && (
                  <p className={`text-sm mt-2 text-center font-medium ${saveMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                    {saveMessage}
                  </p>
                )}
              </div>
            )}

            <button
              onClick={exportPDF}
              className="w-full py-4 sm:py-5 rounded-xl font-bold text-white text-base sm:text-lg transition-all transform hover:scale-105 hover:shadow-xl flex items-center justify-center gap-3"
              style={{ background: brandGradient }}
            >
              <Download className="w-5 h-5 sm:w-6 sm:h-6" />
              Download Proposal
            </button>

            {estimate && proposalStatus === 'pending' && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <button
                  onClick={() => {
                    console.log('State before accept:', { showContract, estimate: !!estimate, brand: !!brand, proposalStatus });
                    handleAcceptProposal();
                  }}
                  className="py-4 rounded-xl font-bold text-white text-base transition-all transform hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-5 h-5" />
                  Accept Proposal
                </button>
                <button
                  onClick={() => setShowRejectionModal(true)}
                  className="py-4 rounded-xl font-bold text-white text-base transition-all transform hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-5 h-5" />
                  Reject Proposal
                </button>
              </div>
            )}

            {estimate && proposalStatus === 'accepted' && (
              <div className="mt-4 p-4 bg-green-50 border-2 border-green-500 rounded-xl text-center">
                <div className="flex items-center justify-center gap-2 text-green-700 font-semibold text-lg">
                  <CheckCircle className="w-6 h-6" />
                  <span>Proposal Accepted</span>
                </div>
                <p className="text-sm text-green-600 mt-2">Thank you for accepting this proposal!</p>
              </div>
            )}

            {estimate && proposalStatus === 'rejected' && (
              <div className="mt-4 p-4 bg-red-50 border-2 border-red-500 rounded-xl text-center">
                <div className="flex items-center justify-center gap-2 text-red-700 font-semibold text-lg">
                  <XCircle className="w-6 h-6" />
                  <span>Proposal Declined</span>
                </div>
                <p className="text-sm text-red-600 mt-2">Thank you for your feedback.</p>
              </div>
            )}

            {!estimate && (
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Apply Discount Code</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-all outline-none text-sm"
                  />
                  <button
                    onClick={applyDiscountCode}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-semibold"
                  >
                    Apply
                  </button>
                </div>
                {discountError && (
                  <p className="text-xs text-red-600 mt-2">{discountError}</p>
                )}
                {appliedDiscount && (
                  <p className="text-xs text-green-600 mt-2 font-medium">Discount "{appliedDiscount.code}" applied!</p>
                )}
              </div>
            )}
          </div>
        </div>

        {brand.terms_text && (
          <div className="mt-6 text-xs sm:text-sm text-gray-500 text-center px-4 leading-relaxed">
            <p>{brand.terms_text}</p>
          </div>
        )}

      </div>
    </div>
    </>
  );
}
